import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Leaf, Clock, CheckCircle2, Loader2 } from '../lib/icons';
import { lirePortail } from '../lib/portail';

const fmt = (v, d = 0) =>
  new Intl.NumberFormat('fr-FR', { minimumFractionDigits: d, maximumFractionDigits: d }).format(Number(v) || 0);

const ETAPES = ['En attente', 'En trituration', 'Terminé / Prêt au retrait'];

const etapeIndex = (statut) => {
  const s = String(statut || '').toLowerCase();
  if (s.includes('trituré') || s.includes('triture') || s.includes('décompté') || s.includes('decompte')) return 2;
  if (s.includes('trituration')) return 1;
  return 0;
};

export default function PortailLot() {
  const { token } = useParams();
  const [doc, setDoc] = useState(undefined);

  useEffect(() => {
    lirePortail(token).then((d) => setDoc(d ?? null));
  }, [token]);

  if (doc === undefined)
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0d1f16] text-emerald-50">
        <Loader2 className="animate-spin" />
      </div>
    );

  if (!doc)
    return (
      <div data-testid="portail-introuvable" className="flex min-h-screen flex-col items-center justify-center gap-2 bg-[#0d1f16] px-6 text-center text-emerald-50">
        <p className="font-heading text-xl font-black">Lien introuvable</p>
        <p className="text-sm text-emerald-200/80">Ce lien de suivi n'est plus valide. Contactez l'huilerie.</p>
      </div>
    );

  const p = doc.payload;
  const idx = etapeIndex(p.statut);

  return (
    <div data-testid="portail-lot" className="min-h-screen bg-[#0d1f16] px-4 py-8 text-emerald-50">
      <div className="mx-auto w-full max-w-md space-y-6">
        <header className="flex items-center gap-3 border-b border-emerald-800/60 pb-5">
          {p.logo && <img src={p.logo} alt="Logo" className="h-14 w-14 rounded bg-white/95 object-contain p-1" />}
          <div>
            <p className="font-heading text-lg font-black leading-tight">{p.huilerie}</p>
            <p className="font-data text-[11px] uppercase tracking-[0.14em] text-emerald-300">Suivi de lot en ligne</p>
          </div>
        </header>

        <section className="border border-emerald-800/60 bg-emerald-950/40 p-5">
          <p className="font-data text-[11px] uppercase tracking-[0.14em] text-emerald-300">Bon d'entrée</p>
          <p data-testid="portail-lot-numero" className="font-heading text-3xl font-black tracking-tight">{p.id}</p>
          <p className="mt-1 text-sm text-emerald-100/90">
            {p.tiers} · reçu le {p.date}
          </p>
        </section>

        <section className="grid grid-cols-2 gap-px bg-emerald-800/60">
          {[
            ['Poids brut', `${fmt(p.poidsBrut)} Kg`],
            ['Tare', `${fmt(p.tare)} Kg`],
            ['Poids net', `${fmt(p.poidsNet)} Kg`],
            ['Parcours', p.parcours === 'client' ? 'Trituration' : 'Achat d’olives'],
          ].map(([k, v]) => (
            <div key={k} className="bg-[#0d1f16] px-4 py-3">
              <p className="text-[11px] uppercase tracking-[0.08em] text-emerald-400">{k}</p>
              <p data-testid={`portail-${k.toLowerCase().replace(/\s/g, '-')}`} className="font-data text-lg font-semibold">{v}</p>
            </div>
          ))}
        </section>

        <section data-testid="portail-statut" className="border border-emerald-800/60 p-5">
          <p className="font-data text-[11px] uppercase tracking-[0.14em] text-emerald-300">Statut du lot</p>
          <ul className="mt-3 space-y-3">
            {ETAPES.map((e, i) => {
              const fait = i <= idx;
              return (
                <li key={e} className="flex items-center gap-3">
                  {fait ? (
                    <CheckCircle2 size={18} className="flex-shrink-0 text-emerald-400" />
                  ) : (
                    <Clock size={18} className="flex-shrink-0 text-emerald-800" />
                  )}
                  <span className={`text-sm ${fait ? 'font-semibold text-emerald-50' : 'text-emerald-200/40'}`}>{e}</span>
                </li>
              );
            })}
          </ul>
          <p className="mt-4 border-l-2 border-emerald-400 bg-emerald-900/40 px-3 py-2 font-data text-sm">
            {p.statut}
          </p>
        </section>

        <footer className="flex items-center gap-2 pt-2 text-[11px] text-emerald-300/70">
          <Leaf size={13} /> Mis à jour le {String(doc.maj).slice(0, 10)} · {p.huilerie}
        </footer>
      </div>
    </div>
  );
}
